'use client';

import type { MediaDimensions } from '@/components/InfiniteCanvas/utils/mediaDimensions';
import {
  buildImageMediaHealth,
  IMAGE_MEDIA_HEALTH_ASPECT_RATIO_MAX,
  IMAGE_MEDIA_HEALTH_ASPECT_RATIO_MIN,
  IMAGE_MEDIA_HEALTH_HARD_MAX_FILE_BYTES,
  IMAGE_MEDIA_HEALTH_MAX_LONG_EDGE,
  IMAGE_MEDIA_HEALTH_MAX_TOTAL_PIXELS,
  IMAGE_MEDIA_HEALTH_TARGET_MAX_FILE_BYTES,
  type ImageMediaHealth,
  shouldWarnForAspectRatio,
} from './mediaHealth';

export interface ImageRequestFileMeta extends MediaDimensions {
  fileSize?: number;
  mimeType?: string;
}

export const ASPECT_RATIO_MIN = IMAGE_MEDIA_HEALTH_ASPECT_RATIO_MIN;
export const ASPECT_RATIO_MAX = IMAGE_MEDIA_HEALTH_ASPECT_RATIO_MAX;

export interface BuildUploadSafeImageResult {
  file: File;
  dimensions: MediaDimensions;
  transformed: boolean;
  aspectRatioWarning: boolean;
  mediaHealth: ImageMediaHealth;
}

export const UPLOAD_SAFE_IMAGE_MAX_TOTAL_PIXELS = IMAGE_MEDIA_HEALTH_MAX_TOTAL_PIXELS;
export const UPLOAD_SAFE_IMAGE_MAX_LONG_EDGE = IMAGE_MEDIA_HEALTH_MAX_LONG_EDGE;
export const UPLOAD_SAFE_IMAGE_TARGET_MAX_FILE_BYTES = IMAGE_MEDIA_HEALTH_TARGET_MAX_FILE_BYTES;
export const UPLOAD_SAFE_IMAGE_HARD_MAX_FILE_BYTES = IMAGE_MEDIA_HEALTH_HARD_MAX_FILE_BYTES;

const OUTPUT_QUALITY_CANDIDATES = [0.9, 0.82, 0.74, 0.66, 0.58];
const EXTRA_DOWNSCALE_FACTORS = [1, 0.92, 0.84, 0.76];

export function isUploadSafeImage(fileMeta: ImageRequestFileMeta) {
  const { width, height, fileSize } = fileMeta;
  if (!Number.isFinite(width) || !Number.isFinite(height) || width <= 0 || height <= 0) {
    return false;
  }

  const totalPixels = width * height;
  const longEdge = Math.max(width, height);

  if (totalPixels > UPLOAD_SAFE_IMAGE_MAX_TOTAL_PIXELS) {
    return false;
  }

  if (longEdge > UPLOAD_SAFE_IMAGE_MAX_LONG_EDGE) {
    return false;
  }

  if (typeof fileSize === 'number' && fileSize > UPLOAD_SAFE_IMAGE_TARGET_MAX_FILE_BYTES) {
    return false;
  }

  return true;
}

export async function buildUploadSafeImage(
  fileOrUrl: File | string,
  options: {
    onTransformStart?: () => void;
  } = {},
): Promise<BuildUploadSafeImageResult> {
  const { onTransformStart } = options;
  const sourceFile = typeof fileOrUrl === 'string'
    ? await buildFileFromUrl(fileOrUrl)
    : fileOrUrl;
  const sourceImage = await loadImageFromFile(sourceFile);
  const sourceMeta: ImageRequestFileMeta = {
    width: sourceImage.naturalWidth,
    height: sourceImage.naturalHeight,
    fileSize: sourceFile.size,
    mimeType: sourceFile.type,
  };

  const hasAspectRatioWarning = shouldWarnForAspectRatio(sourceMeta.width, sourceMeta.height);

  console.log('[uploadSafeImage] source:', {
    name: sourceFile.name,
    width: sourceMeta.width,
    height: sourceMeta.height,
    size: sourceFile.size,
    mimeType: sourceMeta.mimeType,
  });

  const isSafe = isUploadSafeImage(sourceMeta);
  console.log('[uploadSafeImage] isSafe:', isSafe, '| ratio:', (sourceMeta.width / sourceMeta.height).toFixed(3));

  if (isSafe) {
    const mediaHealth = buildImageMediaHealth({
      width: sourceMeta.width,
      height: sourceMeta.height,
    });

    return {
      file: sourceFile,
      dimensions: {
        width: sourceMeta.width,
        height: sourceMeta.height,
      },
      transformed: false,
      aspectRatioWarning: hasAspectRatioWarning,
      mediaHealth,
    };
  }

  const baseDimensions = getUploadSafeDimensions(sourceMeta);
  const outputMimeType = getPreferredOutputMimeType(sourceFile.type);
  console.log('[uploadSafeImage] baseDimensions:', baseDimensions, '| outputMimeType:', outputMimeType);
  onTransformStart?.();

  for (const downscaleFactor of EXTRA_DOWNSCALE_FACTORS) {
    const width = clampDimension(Math.round(baseDimensions.width * downscaleFactor));
    const height = clampDimension(Math.round(baseDimensions.height * downscaleFactor));
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;

    const context = canvas.getContext('2d');
    if (!context) {
      throw new Error('Failed to initialize image canvas');
    }

    context.imageSmoothingEnabled = true;
    context.imageSmoothingQuality = 'high';
    context.drawImage(sourceImage, 0, 0, width, height);

    for (const quality of OUTPUT_QUALITY_CANDIDATES) {
      const blob = await canvasToBlob(canvas, outputMimeType, quality);
      console.log('[uploadSafeImage] try:', { downscaleFactor, quality, width, height, blobSize: blob.size });

      if (blob.size > UPLOAD_SAFE_IMAGE_HARD_MAX_FILE_BYTES) {
        continue;
      }

      const outputFile = new File(
        [blob],
        replaceFileExtension(sourceFile.name, blob.type),
        {
          type: blob.type,
          lastModified: Date.now(),
        },
      );

      if (
        blob.size <= UPLOAD_SAFE_IMAGE_TARGET_MAX_FILE_BYTES
        || downscaleFactor !== EXTRA_DOWNSCALE_FACTORS[0]
      ) {
        const mediaHealth = buildImageMediaHealth({ width, height });
        console.log('[uploadSafeImage] success:', { width, height, outputSize: blob.size });
        return {
          file: outputFile,
          dimensions: { width, height },
          transformed: true,
          aspectRatioWarning: hasAspectRatioWarning,
          mediaHealth,
        };
      }
    }
  }

  console.error('[uploadSafeImage] failed: could not compress within limits');
  throw new Error('Unable to optimize oversized image within safe upload limits');
}

export function getUploadSafeDimensions(fileMeta: ImageRequestFileMeta) {
  const { width, height } = fileMeta;
  const totalPixels = width * height;
  const longEdge = Math.max(width, height);
  const scaleByPixels = Math.sqrt(UPLOAD_SAFE_IMAGE_MAX_TOTAL_PIXELS / totalPixels);
  const scaleByLongEdge = UPLOAD_SAFE_IMAGE_MAX_LONG_EDGE / longEdge;
  const scale = Math.min(scaleByPixels, scaleByLongEdge, 1);

  return {
    width: clampDimension(Math.round(width * scale)),
    height: clampDimension(Math.round(height * scale)),
  };
}

async function buildFileFromUrl(url: string) {
  const response = await fetch(url, { cache: 'no-store' });
  if (!response.ok) {
    throw new Error(`Failed to fetch image: ${response.status}`);
  }

  const blob = await response.blob();
  const fileName = resolveFileNameFromUrl(url, blob.type);
  return new File([blob], fileName, {
    type: blob.type || 'image/jpeg',
    lastModified: Date.now(),
  });
}

function resolveFileNameFromUrl(url: string, mimeType: string) {
  try {
    const parsedUrl = new URL(url);
    const pathname = parsedUrl.pathname.split('/').filter(Boolean).pop() || 'image';
    return ensureFileExtension(pathname, mimeType);
  } catch {
    return ensureFileExtension('image', mimeType);
  }
}

function ensureFileExtension(fileName: string, mimeType: string) {
  if (/\.[^.]+$/u.test(fileName)) {
    return fileName;
  }

  return replaceFileExtension(fileName, mimeType);
}

function replaceFileExtension(fileName: string, mimeType: string) {
  const baseName = fileName.replace(/\.[^.]+$/u, '');
  return `${baseName}.${getFileExtension(mimeType)}`;
}

function getPreferredOutputMimeType(mimeType: string) {
  if (mimeType === 'image/jpeg' || mimeType === 'image/jpg') {
    return 'image/jpeg';
  }

  if (mimeType === 'image/png') {
    return 'image/png';
  }

  if (mimeType === 'image/webp') {
    return 'image/webp';
  }

  return 'image/jpeg';
}

function getFileExtension(mimeType: string) {
  switch (mimeType) {
    case 'image/jpeg':
      return 'jpg';
    case 'image/png':
      return 'png';
    case 'image/webp':
      return 'webp';
    default:
      return 'jpg';
  }
}

function clampDimension(value: number) {
  return Math.max(1, value);
}

async function loadImageFromFile(file: File) {
  const objectUrl = URL.createObjectURL(file);

  try {
    return await new Promise<HTMLImageElement>((resolve, reject) => {
      const image = new Image();
      image.decoding = 'async';
      image.onload = () => resolve(image);
      image.onerror = () => reject(new Error('Failed to decode image'));
      image.src = objectUrl;
    });
  } finally {
    URL.revokeObjectURL(objectUrl);
  }
}

async function canvasToBlob(
  canvas: HTMLCanvasElement,
  mimeType: string,
  quality: number,
) {
  const primaryBlob = await new Promise<Blob | null>((resolve) => {
    canvas.toBlob(resolve, mimeType, quality);
  });

  if (primaryBlob) {
    return primaryBlob;
  }

  const fallbackBlob = await new Promise<Blob | null>((resolve) => {
    canvas.toBlob(resolve, 'image/jpeg', quality);
  });

  if (!fallbackBlob) {
    throw new Error('Failed to export optimized image');
  }

  return fallbackBlob;
}
